import React, { useEffect, useState, useCallback } from "react";
import {
  Modal,
  ModalHeader,
  Spinner,
  Button,
  Col,
  Container,
  Row,
  ModalBody,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import "assets/scss/custom.scss";
import PricingSection from "./Cart/PricingSection";
import {
  emptyOrderAction,
  removeFromOrder,
  updateTableData,
} from "store/actions/cartAction";
import SelectItemsOffers from "./AddOrder/CustomMenuList";
import ItemCard from "./AddOrder/ItemCard";
import OfferCard from "./AddOrder/OfferCard";
import { addOrderAction } from "store/actions/orderActions";
import { toast } from "react-toastify";
import TableSelection from "./TableSelection";
import CustomerData from "./AddOrder/CustomerData";
import { addCustomer } from "store/actions/customerAction";

const AddOrderModal = ({ isOpen, toggler }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { restaurantsDetail } = useSelector((state) => state.restaurant);
  const { orderItems } = useSelector((state) => state.cart);

  const [selectedItems, setSelectedItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [tableNumber, setTableNumber] = useState("");
  const { allTablesData } = useSelector((state) => state.table);
  const [isValid, setIsValid] = useState(false);
  const [customerAddError, setCustomerAddError] = useState("");
  const [hasAnyItemAddon, setHasAnyItemAddon] = useState(false);

  const [customerData, setCustomerData] = useState({
    name: "",
    phoneNo: "",
    gender: "",
    addToExisting: false,
  });
  const [fieldErrors, setFieldErros] = useState({
    name: "",
    phone: "",
    gender: "",
  });

  const [orderData, setOrderData] = useState({
    basePrice: 0,
    finalPrice: 0,
    appliedCharges: {},
  });

  const restaurantID = user?.restaurantID;
  const bgColor = restaurantsDetail?.adminColor;
  const currency = restaurantsDetail?.currency || "$";

  useEffect(() => {
    setSelectedItems(orderItems);
    if (orderItems?.some((item) => item.selectedAddons?.length > 0)) {
      setHasAnyItemAddon(true);
    } else {
      setHasAnyItemAddon(false);
    }
  }, [orderItems]);

  useEffect(() => {
    if (customerAddError !== "") {
      setCustomerAddError("");
    }
  }, [customerData.phoneNo, customerData.addToExisting]);

  useEffect(() => {
    if (!restaurantsDetail || orderItems.length === 0) return;

    const totalAmount = orderItems?.reduce((acc, item) => {
      const price = parseFloat(item.totalPrice) || 0;
      return acc + price;
    }, 0);

    //Base price value rounded to 2 digits
    const roundedTotal = parseFloat(totalAmount.toFixed(2));

    //Now calculating final price from applied charges
    let basePrice = parseFloat(totalAmount.toFixed(2));
    let final = basePrice;
    let charges = [];

    restaurantsDetail?.additionalCharges?.forEach((charge) => {
      if (charge.isActive) {
        const fee = Number(charge.fee) || 0;
        let addedAmount = charge.type === "fixed" ? fee : (final * fee) / 100;

        final += addedAmount;
        charges.push({
          name: charge.name || "Additional Charge",
          type: charge.type,
          fee,
          addedAmount: addedAmount.toFixed(2),
        });
      }
    });
    setOrderData(() => ({
      basePrice: roundedTotal,
      finalPrice: final,
      appliedCharges: charges,
    }));
  }, [orderItems, restaurantsDetail]);

  const tablesDropdown = useCallback(() => {
    const currentTime = new Date().getTime();

    return Object.keys(allTablesData?.allTables || {})
      .map((key) => {
        const table = allTablesData?.allTables[key];
        const isExpired =
          table.expireAt && currentTime > table.expireAt.toMillis();
        const isOccupied = !!table.occupiedAt;

        // Hide only if table is occupied AND not expired
        if (isOccupied && !isExpired) return null;

        return {
          ...table,
          isExpired,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.tableNo - b.tableNo);
  }, [allTablesData]);

  const handleRemoveItem = (orderItemID) => {
    dispatch(removeFromOrder(orderItemID));
  };

  const handlePay = (e) => {
    e.preventDefault();

    confirmOrder();
  };

  const confirmOrder = async () => {
    setCustomerAddError("");
    setIsLoading(true);
    const transformedOrder = orderItems.map(
      ({
        orderItemID,
        value,
        addons,
        selectedAddons,
        instructions,
        ...item
      }) => {
        const { type, ...rest } = item;

        return {
          ...rest,
          ...(type !== "item" && { type }),
          price: Number(item.price) || 0,
          totalPrice: Number(item.totalPrice) || 0,
          addons: selectedAddons || [],
          notes: instructions || "",
        };
      }
    );

    const {
      success,
      customerID,
      error = "",
    } = await dispatch(addCustomer(customerData));

    if (!success) {
      setCustomerAddError(error);
      return;
    }

    const appliedCharges = [...orderData.appliedCharges];
    const orderPayload = {
      appliedCharges,

      currency: currency || "",
      customerID: customerID || "",
      customerName: customerData.name || "",

      order: transformedOrder,

      orderType: "dinein",
      waiterID: user?.id || "nil",
      paymentMethod: "",
      paymentRecieved: false,
      restaurantID: restaurantID || "nil",
      status: "pending",
      tableNumber: tableNumber || "",
      timer: allTablesData?.timer || "",
      tip: null,
      tipType: "",
      totalAmount: Number(orderData?.basePrice) || 0, // or calculate if needed
      finalPrice: Number(orderData?.finalPrice) || 0,
    };

    const onSuccess = () => {
      toast.success("Order placed successfully");
      dispatch(emptyOrderAction());
      toggler();
      setIsLoading(false);

      dispatch(
        updateTableData(
          user?.restaurantID,
          customerID,
          tableNumber,
          allTablesData?.timer,
          restaurantsDetail?.name
        )
      );
      setTableNumber("");
    };

    const onError = (errorMessage) => {
      toast.error("Error while placing order");
      setIsLoading(false);
    };

    const orderId = await dispatch(
      addOrderAction(orderPayload, onSuccess, onError)
    );
    return orderId;
  };

  const selectedOnlyItems = selectedItems?.filter(
    (item) => item?.type === "item"
  );
  const selectedOnlyOffers = selectedItems?.filter(
    (item) => item?.type !== "item"
  );

  return (
    <Modal isOpen={isOpen} toggle={toggler} size="xl">
      <ModalHeader
        toggle={toggler}
        className="align-items-center border-bottom py-3"
      >
        Add Order
      </ModalHeader>

      <ModalBody>
        <Container>
          <SelectItemsOffers currency={currency} restaurantID={restaurantID} />

          {selectedItems && selectedItems?.length > 0 ? (
            <>
              <Row className="w-100 mx-0 mt-0 mb-2 py-0">
                <Col className="col-12 mb-0 px-0 d-flex flex-row justify-content-between align-items-end">
                  <div className="fs--14 d-flex flex-row align-items-center">
                    <div className="fw--500">Order Items:&nbsp;</div>
                    <div
                      className="fw--600 px-2 d-flex align-items-center justify-content-center"
                      style={{
                        backgroundColor: bgColor,
                        borderRadius: "9999px",
                        color: "#fff",
                      }}
                    >
                      {selectedItems?.length}
                    </div>
                  </div>
                  <Button
                    style={{
                      backgroundColor: "rgb(215, 24, 40)",
                      color: "#fff",
                    }}
                    className="top_cards__addtocart btn-sm fs--12 fw--500 border-0"
                    onClick={() => {
                      dispatch(emptyOrderAction());
                    }}
                    disabled={isLoading}
                  >
                    Remove all Items
                  </Button>
                </Col>
              </Row>
              <Row className="w-100 mx-0">
                <Col className="col-12 mb-0 px-0 border-top border-bottom border-lighter">
                  {/* Row for Items */}
                  {selectedOnlyItems?.length > 0 && (
                    <Row className="p-0 mt-3">
                      {selectedOnlyItems.map((item) => (
                        <Col
                          key={item?.id}
                          xl={3}
                          lg={4}
                          md={6}
                          sm={6}
                          xs={12}
                          style={{ marginBottom: "19px" }}
                        >
                          <ItemCard
                            item={item}
                            handleRemoveItem={handleRemoveItem}
                            hasAnyItemAddon={hasAnyItemAddon}
                          />
                        </Col>
                      ))}

                      <Col className="col-12">
                        <div
                          className={`w-100 ${
                            selectedOnlyOffers?.length > 0
                              ? "border-bottom"
                              : ""
                          }`}
                        ></div>
                      </Col>
                    </Row>
                  )}

                  {/* Row for Offers */}
                  {selectedOnlyOffers?.length > 0 && (
                    <Row className="p-0 mt-3">
                      {selectedOnlyOffers.map((item) => (
                        <Col
                          key={item?.id}
                          xl={3}
                          lg={4}
                          md={6}
                          sm={6}
                          xs={12}
                          style={{ marginBottom: "19px" }}
                        >
                          <OfferCard
                            offer={item}
                            handleRemoveItem={handleRemoveItem}
                          />
                        </Col>
                      ))}
                    </Row>
                  )}
                </Col>

                <Col className="col-12">
                  <PricingSection
                    currency={currency || "$"}
                    finalPrice={orderData.finalPrice}
                    basePrice={orderData.basePrice}
                    appliedCharges={orderData.appliedCharges}
                  />
                </Col>

                <Col className="col-12">
                  <CustomerData
                    customerData={customerData}
                    setCustomerData={setCustomerData}
                    fieldErrors={fieldErrors}
                    setFieldErros={setFieldErros}
                    setIsValid={setIsValid}
                    bgColor={bgColor}
                  />
                </Col>
                {customerAddError?.length > 0 && (
                  <Col className="col-12 justify-content-start fs--12 text-danger px-0 mt-2">
                    <div>{customerAddError}</div>
                  </Col>
                )}

                <Col className="col-12 d-flex justify-content-center mt-2">
                  <Col xl={5} lg={7} md={9} sm={10} xs={12} className="">
                    <TableSelection
                      tables={tablesDropdown()}
                      tableNumber={tableNumber}
                      setTableNumber={setTableNumber}
                      bgColor={bgColor}
                    />
                  </Col>
                </Col>
                <Col className="col-12 d-flex justify-content-center mt-3">
                  <Col className="col-12 col-sm-10 col-md-8 col-lg-5 col-xl-4 px-0 d-flex justify-content-center">
                    <button
                      className="top_cards__addtocart fs--16 fw--500 w-100 btn text-white p-3"
                      style={{ backgroundColor: bgColor }}
                      onClick={handlePay}
                      disabled={isLoading || tableNumber === "" || !isValid}
                    >
                      {isLoading ? (
                        <Spinner size="sm" color="white" />
                      ) : (
                        ` Confirm Order - ${currency || "$"}
                      ${orderData?.finalPrice}`
                      )}
                    </button>
                  </Col>
                </Col>
              </Row>
            </>
          ) : (
            <div className="w-100 d-flex flex-column justify-content-center align-items-center text-muted py-4 bg-secondary rounded">
              <i className="fas fa-box-open fs--24 mb-2" />
              <div className="fs--16 fw--400">Nothing Added Yet</div>
            </div>
          )}
        </Container>
      </ModalBody>
    </Modal>
  );
};

export default AddOrderModal;
